/* Enhance the native navigation: inline mobile menus and desktop hover dropdowns. */
(() => {
  if (window.hcMobileHeader) return;
  window.hcMobileHeader = true;
  const mobile = window.matchMedia('(max-width: 992px)');
  const hoverPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const instances = new Map();
  const desktopInstances = new Map();

  function enhanceDesktop(header) {
    if (desktopInstances.has(header)) return;
    const navigation = header.querySelector('.header__inline-menu');
    if (!navigation) return;
    const menus = [...navigation.querySelectorAll('header-menu > details')];
    if (!menus.length) return;

    const controller = new AbortController();
    const listen = { signal: controller.signal };

    function sync(menu) {
      const summary = menu.querySelector(':scope > summary');
      summary?.setAttribute('aria-expanded', String(menu.open));
      if (summary?.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    }

    function close(menu, focus = false) {
      menu.open = false;
      sync(menu);
      menu.querySelectorAll('details[open]').forEach((child) => {
        child.open = false;
        sync(child);
      });
      if (focus) menu.querySelector(':scope > summary')?.focus();
    }

    function closeOthers(keep) {
      menus.forEach((menu) => {
        if (menu !== keep && menu.open) close(menu);
      });
    }

    menus.forEach((menu) => {
      const owner = menu.parentElement;
      // A short grace period before closing lets the pointer cross the gap between the menu item and its panel.
      let closeTimer = 0;
      owner.addEventListener('pointerenter', (event) => {
        if (mobile.matches || !hoverPointer.matches || event.pointerType === 'touch') return;
        clearTimeout(closeTimer);
        if (menus.some((other) => other !== menu && other.contains(document.activeElement))) return;
        closeOthers(menu);
        menu.open = true;
        sync(menu);
      }, listen);
      owner.addEventListener('pointerleave', (event) => {
        if (mobile.matches || !hoverPointer.matches || event.pointerType === 'touch') return;
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => {
          if (controller.signal.aborted || owner.matches(':hover')) return;
          if (owner.contains(document.activeElement)) document.activeElement.blur();
          close(menu);
        }, 320);
      }, listen);
      owner.addEventListener('focusout', () => {
        // Focus has settled before deciding whether the keyboard left this menu.
        setTimeout(() => {
          if (!controller.signal.aborted && !mobile.matches && !owner.contains(document.activeElement)) close(menu);
        });
      }, listen);
      [menu, ...menu.querySelectorAll('details')].forEach((item) => {
        item.addEventListener('toggle', () => {
          sync(item);
          if (!mobile.matches && item === menu && item.open) closeOthers(item);
        }, listen);
        sync(item);
      });
    });

    // Leave native summary click/Enter/Space behavior in place, including touch.
    navigation.addEventListener('click', (event) => {
      if (mobile.matches) return;
      const menu = event.target.closest('summary')?.parentElement;
      if (!menus.includes(menu)) return;
      // With a mouse, hovering already opened the menu, so a click on its title keeps it open instead of toggling it shut.
      if (menu.open && hoverPointer.matches && event.detail > 0) {
        event.preventDefault();
        return;
      }
      if (!menu.open) closeOthers(menu);
    }, { ...listen, capture: true });

    navigation.addEventListener('keyup', (event) => {
      if (mobile.matches || event.key !== 'Escape') return;
      const menu = event.target.closest('details[open]');
      if (!menu || !navigation.contains(menu)) return;
      // Dawn installs bubbling Escape listeners on each details element.
      // Handle the nearest open menu once, then return focus to its summary.
      event.preventDefault();
      event.stopImmediatePropagation();
      close(menu, true);
    }, { ...listen, capture: true });

    document.addEventListener('click', (event) => {
      if (mobile.matches) return;
      menus.forEach((menu) => {
        if (menu.open && !menu.contains(event.target)) close(menu);
      });
    }, listen);

    mobile.addEventListener('change', () => {
      const moveFocus = mobile.matches && navigation.contains(document.activeElement);
      closeOthers();
      if (moveFocus) header.querySelector('.menu-drawer-container > summary')?.focus();
    }, listen);

    desktopInstances.set(header, () => {
      controller.abort();
      closeOthers();
      desktopInstances.delete(header);
    });
  }

  function enhance(drawer) {
    if (instances.has(drawer)) return;
    const header = drawer.closest('.hc-mobile-header');
    const details = drawer.querySelector('.menu-drawer-container');
    const trigger = details?.querySelector(':scope > summary');
    const utilitySlot = drawer.querySelector('[data-hc-header-utilities]');
    if (!header || !details || !trigger || !utilitySlot) return;

    const controller = new AbortController();
    const listen = { signal: controller.signal };
    const innerEscapeEvents = new WeakSet();
    const icons = header.querySelector('.header > .header__icons');
    const iconPosition = document.createComment('HC original header utilities');
    if (icons) icons.before(iconPosition);
    const submenus = [...drawer.querySelectorAll('.menu-drawer__navigation details')].map((menu) => {
      const summary = menu.querySelector(':scope > summary');
      return {
        menu,
        wasOpen: menu.open,
        summary,
        tabIndex: summary?.getAttribute('tabindex'),
        ariaDisabled: summary?.getAttribute('aria-disabled'),
      };
    });
    let active = false;

    function syncExpanded() {
      trigger.setAttribute('aria-expanded', String(details.open));
      trigger.setAttribute('aria-controls', 'menu-drawer');
    }

    function close(focus = false) {
      details.open = false;
      details.classList.remove('menu-opening');
      syncExpanded();
      if (focus) trigger.focus();
    }

    function closeUtilities(focus = true) {
      icons?.querySelectorAll('details-modal').forEach((modal) => {
        if (modal.querySelector('details[open]') && typeof modal.close === 'function') modal.close(focus);
      });
      drawer.querySelectorAll('localization-form').forEach((form) => {
        if (form.querySelector('button[aria-expanded="true"]') && typeof form.hidePanel === 'function') form.hidePanel();
      });
    }

    function applyMode() {
      const next = mobile.matches;
      if (active === next) return;
      const returnToDesktop = active && !next && drawer.contains(document.activeElement) && !icons?.contains(document.activeElement);
      if (active && !next) closeUtilities();
      close();
      active = next;
      submenus.forEach(({ menu, summary, wasOpen, tabIndex, ariaDisabled }) => {
        menu.open = active || wasOpen;
        menu.classList.remove('menu-opening', 'submenu-open');
        summary?.setAttribute('aria-expanded', String(menu.open));
        if (active) {
          summary?.setAttribute('tabindex', '-1');
          summary?.setAttribute('aria-disabled', 'true');
        } else {
          if (tabIndex === null) summary?.removeAttribute('tabindex');
          else summary?.setAttribute('tabindex', tabIndex);
          if (ariaDisabled === null) summary?.removeAttribute('aria-disabled');
          else summary?.setAttribute('aria-disabled', ariaDisabled);
        }
      });
      if (icons) {
        if (active) utilitySlot.append(icons);
        else iconPosition.after(icons);
      }
      if (returnToDesktop) header.querySelector('.header__inline-menu summary, .header__inline-menu a, .header__heading-link')?.focus();
    }

    // Capture only the reference menu interactions before Dawn's modal handlers.
    drawer.addEventListener('click', (event) => {
      if (!mobile.matches) return;
      const summary = event.target.closest('summary');
      if (summary && (summary === trigger || summary.closest('.menu-drawer__navigation'))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (summary === trigger) {
          details.open = !details.open;
          details.classList.toggle('menu-opening', details.open);
          syncExpanded();
        }
        return;
      }
      if (event.target.closest('.menu-drawer__navigation a')) close();
    }, { ...listen, capture: true });

    drawer.addEventListener('keyup', (event) => {
      if (!mobile.matches || event.key !== 'Escape' || !details.open) return;
      // Remember the state before an inner control's own handler closes it.
      if (event.target.closest('.hc-mobile-header-utilities, .menu-drawer__localization')) {
        const searchIsOpen = event.target.closest('details-modal')?.querySelector('details[open]');
        const localizationIsOpen = event.target.closest('localization-form')?.querySelector('button[aria-expanded="true"]');
        const dialogInPath = event.composedPath().some((element) => element.matches?.('[role="dialog"], dialog[open]'));
        if (searchIsOpen || localizationIsOpen || dialogInPath) innerEscapeEvents.add(event);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      close(true);
    }, { ...listen, capture: true });

    // Let an inner search/localization control process Escape first, then stop
    // it reaching Dawn's drawer handler (which did not open this inline menu).
    drawer.querySelectorAll('.hc-mobile-header-utilities, .menu-drawer__localization').forEach((utilities) => {
      utilities.addEventListener('keyup', (event) => {
        if (!mobile.matches || event.key !== 'Escape') return;
        event.stopPropagation();
        if (!innerEscapeEvents.has(event) && !event.defaultPrevented) close(true);
      }, listen);
    });

    details.addEventListener('toggle', syncExpanded, listen);
    mobile.addEventListener('change', applyMode, listen);
    syncExpanded();
    applyMode();

    instances.set(drawer, () => {
      if (active) closeUtilities(false);
      controller.abort();
      if (icons && iconPosition.isConnected) iconPosition.after(icons);
      iconPosition.remove();
      instances.delete(drawer);
    });
  }

  function initialize(root = document) {
    root.querySelectorAll('header-drawer[data-hc-mobile-menu]').forEach(enhance);
    root.querySelectorAll('.hc-mobile-header').forEach(enhanceDesktop);
    if (root.matches?.('.hc-mobile-header')) enhanceDesktop(root);
  }

  // Reading progress along the top edge of the header.
  let progressQueued = false;
  function updateProgress() {
    progressQueued = false;
    const bar = document.querySelector('.hc-header-progress');
    if (!bar) return;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    bar.style.transform = `scaleX(${max > 0 ? Math.min(1, window.scrollY / max) : 0})`;
  }
  function queueProgress() {
    if (progressQueued) return;
    progressQueued = true;
    requestAnimationFrame(updateProgress);
  }
  window.addEventListener('scroll', queueProgress, { passive: true });
  window.addEventListener('resize', queueProgress);
  updateProgress();

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((destroy, drawer) => {
      if (event.target.contains(drawer)) destroy();
    });
    desktopInstances.forEach((destroy, header) => {
      if (event.target.contains(header)) destroy();
    });
  });
})();
